import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, ScanLine, Check, Camera, ArrowRight, X, AlertTriangle, Sparkles, RotateCcw, Loader2, Images } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Reveal, Overline } from "../components/Reveal";
import { COURT_TYPES } from "../data/content";
import { ContactFields, EMPTY_CONTACT, validateContact, trimContact } from "../components/ContactFields";
import { CONVERTED } from "../lib/gtag";
import Seo from "../components/Seo";

const FILE_BASE = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 6;
const MAX_BYTES = 15 * 1024 * 1024;

const TIPS = [
  "Stand at the baseline so the whole surface is in frame.",
  "Shoot in daylight — shadows and dusk hide cracking.",
  "Add one close-up of the worst damage.",
  "Include the edges and any area where water sits.",
];

const STATUS = ["Reading surface texture", "Detecting cracking and coating failure", "Checking line marking", "Assessing drainage evidence", "Forming recommendation"];

const SEVERITY = {
  severe: "border-red-500/40 bg-red-500/10 text-red-300",
  moderate: "border-gold/40 bg-gold/10 text-gold",
  minor: "border-white/15 bg-white/5 text-zinc-300",
};

const CONDITION_TONE = {
  excellent: "text-neon", good: "text-neon", fair: "text-gold",
  poor: "text-orange-400", critical: "text-red-400",
};

const match = (list, value) => list.find((x) => x.toLowerCase() === String(value || "").toLowerCase());

// The AI reports a condition grade (Excellent…Critical); this form asks for the main
// visible ISSUE. Map the AI's defects onto our issue list instead of the grade.
const ISSUE_RULES = [
  [/crack|fissure|split|movement/i, "Cracking"],
  [/peel|delamin|flak|blister|lift/i, "Peeling coating"],
  [/pond|drain|water|puddle|moss|algae/i, "Ponding / drainage"],
  [/fade|faded|discolour|discolor|worn|dull|chalk/i, "Fading"],
];

const issueFromReport = (r) => {
  const text = [...(r.defects || []).map((d) => `${d.name} ${d.detail}`), r.drainage_notes, r.line_marking].join(" ");
  const hit = ISSUE_RULES.find(([re]) => re.test(text));
  return hit ? hit[1] : "Not sure";
};

export default function CourtScan() {
  const nav = useNavigate();
  const camRef = useRef(null);
  const galleryRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [phase, setPhase] = useState("upload"); // upload | analysing | report
  const [report, setReport] = useState(null);
  const [status, setStatus] = useState(0);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState(EMPTY_CONTACT);

  useEffect(() => {
    if (phase !== "analysing") return;
    const t = setInterval(() => setStatus((s) => (s + 1) % STATUS.length), 2200);
    return () => clearInterval(t);
  }, [phase]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFiles = async (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    if (!chosen.length) return;
    if (photos.length + chosen.length > MAX_PHOTOS) return toast.error(`Up to ${MAX_PHOTOS} photos.`);
    const tooBig = chosen.find((f) => f.size > MAX_BYTES);
    if (tooBig) return toast.error(`${tooBig.name} is over 15MB — please choose a smaller photo.`);
    setUploading(true);
    for (const f of chosen) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/leads/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { name: r.data.name, url: r.data.url, path: r.data.path }]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Could not upload ${f.name}`);
      }
    }
    setUploading(false);
  };

  const runScan = async () => {
    if (!photos.length) return toast.error("Add at least one photo of your court.");
    setPhase("analysing");
    setStatus(0);
    try {
      const r = await api.post("/court-scan/analyse", { paths: photos.map((p) => p.path) });
      setReport(r.data);
      setPhase("report");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed. Please try again.");
      setPhase("upload");
    }
  };

  const submit = async () => {
    const err = validateContact(form);
    if (err) return toast.error(err);
    setSending(true);
    try {
      await api.post("/leads", {
        type: "court_scan", ...trimContact(form),
        court_type: match(COURT_TYPES, report.court_type) || null,
        data: {
          main_issue: issueFromReport(report),
          issues: (report.defects || []).map((d) => `${d.name} (${d.severity})`).join(", "),
          site_photos: photos.map((p) => ({ name: p.name, url: p.url, content_type: "image/jpeg" })),
          scan_report: report,
        },
      });
      toast.success("Scan sent — the Speedy team will assess your court.");
      nav("/thank-you", CONVERTED("court_scan"));
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const restart = () => { setPhase("upload"); setReport(null); };

  return (
    <div className="pt-28 pb-24 min-h-screen noise">
      <Seo title="Speedy Court Scan™ — AI Court Assessment | Speedy Resurfacing"
           description="Upload photos of your court and get a real surface assessment: visible defects, condition and whether you need a repaint, resurface or rebuild. Sydney & NSW court resurfacing specialists."
           path="/court-scan" />
      <div className="mx-auto max-w-5xl px-5 md:px-10">
        <Reveal>
          <Overline>Speedy Court Scan™</Overline>
          <h1 className="mt-5 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">SCAN YOUR COURT.</h1>
          <p className="mt-5 max-w-xl text-base text-zinc-300">
            Photograph your court on your phone and our scanner reports what it can actually see — cracking, coating
            failure, drainage and line marking — then tells you whether it needs a repaint, a resurface or a rebuild.
          </p>
        </Reveal>

        <div className="mt-10 rounded-lg border border-white/10 bg-obsidian-800 overflow-hidden">
          {/* ---------- UPLOAD ---------- */}
          {phase === "upload" && (
            <div className="p-6 md:p-10" data-testid="scan-upload">
              <div className="grid gap-3 sm:grid-cols-2">
                <button onClick={() => camRef.current?.click()} data-testid="scan-camera-btn"
                        className="group flex items-center gap-4 rounded-lg border border-dashed border-white/15 bg-obsidian p-6 text-left hover:border-neon transition-colors">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-neon/10 text-neon group-hover:bg-neon group-hover:text-obsidian transition-colors">
                    {uploading ? <Loader2 size={22} className="animate-spin" /> : <Camera size={22} />}
                  </span>
                  <span>
                    <span className="block font-heading font-bold text-lg">Take a photo</span>
                    <span className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Use your phone camera</span>
                  </span>
                </button>
                <button onClick={() => galleryRef.current?.click()} data-testid="scan-upload-zone"
                        className="group flex items-center gap-4 rounded-lg border border-dashed border-white/15 bg-obsidian p-6 text-left hover:border-gold transition-colors">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold/10 text-gold group-hover:bg-gold group-hover:text-obsidian transition-colors">
                    {uploading ? <Loader2 size={22} className="animate-spin" /> : <Images size={22} />}
                  </span>
                  <span>
                    <span className="block font-heading font-bold text-lg">Choose photos</span>
                    <span className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Up to {MAX_PHOTOS} · JPG, PNG, WEBP</span>
                  </span>
                </button>
              </div>
              <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onFiles} className="hidden" data-testid="scan-camera-input" />
              <input ref={galleryRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="scan-file-input" />

              {photos.length > 0 && (
                <div className="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-4" data-testid="scan-thumbs">
                  {photos.map((p, i) => (
                    <div key={p.path} className="group relative overflow-hidden rounded-sm border border-white/10">
                      <img src={FILE_BASE + p.url} alt={p.name} className="h-24 w-full object-cover" data-testid={`scan-thumb-${i}`} />
                      <button onClick={() => setPhotos((prev) => prev.filter((_, x) => x !== i))} data-testid={`scan-remove-${i}`}
                              className="absolute right-1.5 top-1.5 rounded-full bg-obsidian/80 p-1 text-zinc-300 hover:text-red-400 transition-colors" aria-label="Remove photo">
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-7 rounded-lg border border-white/10 bg-obsidian p-5">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">For an accurate scan</span>
                <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                  {TIPS.map((t) => (
                    <li key={t} className="flex items-start gap-2 text-sm text-zinc-400"><Check size={14} className="mt-0.5 shrink-0 text-neon" /> {t}</li>
                  ))}
                </ul>
              </div>

              <button onClick={runScan} disabled={!photos.length || uploading} data-testid="scan-run-btn"
                      className="group mt-7 inline-flex w-full items-center justify-between gap-3 rounded-sm bg-neon px-7 py-5 font-heading font-black text-lg text-obsidian shadow-neon hover:bg-neon-hover disabled:opacity-40 transition-colors sm:w-auto">
                {uploading ? "Uploading photos…" : `Run the Scan${photos.length ? ` · ${photos.length} photo${photos.length > 1 ? "s" : ""}` : ""}`}
                <ScanLine size={22} />
              </button>
            </div>
          )}

          {/* ---------- ANALYSING ---------- */}
          {phase === "analysing" && (
            <div className="relative p-6" data-testid="scan-analysing">
              <div className="relative overflow-hidden rounded-md">
                <img src={FILE_BASE + photos[0].url} alt="Court being analysed" className="h-[320px] w-full object-cover md:h-[420px]" />
                <div className="absolute inset-0 bg-obsidian/50" />
                <div className="absolute inset-x-0 h-1 bg-neon animate-scanline" style={{ boxShadow: "0 0 40px 8px rgba(255,216,74,0.5)" }} />
                <div className="absolute inset-0 grid-lines opacity-30" />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-5 text-center">
                  <span className="glass rounded-sm px-6 py-3 font-mono text-xs tracking-widest text-neon md:text-sm">
                    ANALYSING SURFACE<span className="animate-pulse">…</span>
                  </span>
                  <AnimatePresence mode="wait">
                    <motion.span key={status} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                                 className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
                      {STATUS[status]}
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          )}

          {/* ---------- REPORT ---------- */}
          {phase === "report" && report && !report.usable && (
            <div className="p-8 md:p-12" data-testid="scan-unusable">
              <span className="inline-flex items-center gap-2 rounded-sm border border-orange-400/40 bg-orange-400/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-orange-300">
                <AlertTriangle size={13} /> We need a better photo
              </span>
              <h2 className="mt-5 font-heading font-black text-2xl md:text-3xl tracking-tight">WE WON'T GUESS YOUR COURT.</h2>
              <p className="mt-4 text-zinc-300">{report.unusable_reason}</p>
              {report.retake_advice && (
                <div className="mt-5 rounded-lg border-l-2 border-gold bg-obsidian p-5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">What to do</span>
                  <p className="mt-2 text-sm text-zinc-200">{report.retake_advice}</p>
                </div>
              )}
              <button onClick={restart} data-testid="scan-retake-btn"
                      className="mt-7 inline-flex items-center gap-2 rounded-sm bg-neon px-7 py-4 font-bold text-obsidian shadow-neon hover:bg-neon-hover transition-colors">
                <RotateCcw size={17} /> Add better photos
              </button>
            </div>
          )}

          {phase === "report" && report && report.usable && (
            <div className="p-6 md:p-10" data-testid="scan-report">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neon">
                  <Sparkles size={13} /> Scan complete · {report.photos_analysed} photo{report.photos_analysed > 1 ? "s" : ""} analysed
                </span>
                <button onClick={restart} className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-gold transition-colors" data-testid="scan-rescan-btn">
                  Scan again
                </button>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-3">
                <div className="rounded-lg border border-white/10 bg-obsidian p-6">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Surface condition</span>
                  <p className={`mt-1 font-heading font-black text-3xl ${CONDITION_TONE[String(report.condition).toLowerCase()] || "text-white"}`} data-testid="scan-condition">
                    {report.condition}
                  </p>
                </div>
                <div className="rounded-lg border border-gold/25 bg-obsidian p-6 md:col-span-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Recommended</span>
                  <p className="mt-1 font-heading font-black text-2xl gold-text" data-testid="scan-recommendation">{report.recommended_service}</p>
                  <p className="mt-2 text-sm text-zinc-300">{report.recommendation_reason}</p>
                </div>
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                {[["Detected court", report.court_type], ["Surface material", report.surface_material], ["Confidence", report.confidence]].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-white/10 bg-obsidian p-5">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{k}</span>
                    <p className="mt-1 text-sm font-bold capitalize">{v || "—"}</p>
                  </div>
                ))}
              </div>

              {(report.defects || []).length > 0 && (
                <div className="mt-8">
                  <h3 className="font-heading font-bold text-xl">What the scan can see</h3>
                  <ul className="mt-4 space-y-3" data-testid="scan-defects">
                    {report.defects.map((d, i) => (
                      <li key={i} data-testid={`scan-defect-${i}`} className="rounded-lg border border-white/10 bg-obsidian p-5">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="font-heading font-bold">{d.name}</span>
                          <span className={`rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${SEVERITY[String(d.severity).toLowerCase()] || SEVERITY.minor}`}>
                            {d.severity}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-zinc-300">{d.detail}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {[["Line marking", report.line_marking], ["Drainage", report.drainage_notes]].map(([k, v]) => v && (
                  <div key={k} className="rounded-lg border border-white/10 bg-obsidian p-5">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{k}</span>
                    <p className="mt-1.5 text-sm text-zinc-300">{v}</p>
                  </div>
                ))}
              </div>

              {(report.observations || []).length > 0 && (
                <ul className="mt-4 space-y-2" data-testid="scan-observations">
                  {report.observations.map((o) => (
                    <li key={o} className="flex gap-3 text-sm text-zinc-400"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" /> {o}</li>
                  ))}
                </ul>
              )}

              <p className="mt-6 rounded-sm border border-white/10 bg-obsidian p-4 font-mono text-[10px] leading-relaxed text-zinc-500">
                VISUAL ASSESSMENT ONLY. THE SCAN REPORTS WHAT IS VISIBLE IN YOUR PHOTOS AND DOES NOT ESTIMATE ANY
                MEASUREMENT — A SPEEDY SPECIALIST MEASURES AND CONFIRMS EVERY PROJECT ON SITE.
              </p>

              {/* Configure + submit */}
              <div className="mt-10 border-t border-white/10 pt-8" data-testid="scan-configure">
                <h3 className="font-heading font-black text-xl md:text-2xl tracking-tight">SEND THIS SCAN TO THE SPEEDY TEAM.</h3>
                <p className="mt-2 text-sm text-zinc-400">
                  Leave your details and we'll take it from here — your photos and this report go straight to the team. The scan
                  graded your surface <span className="text-gold font-bold">{report.condition}</span> — that grade is sent with your enquiry.
                </p>
                <div className="mt-6">
                  <ContactFields form={form} set={set} testid="scan" />
                </div>
                <div className="mt-8 flex flex-wrap gap-4">
                  <button onClick={submit} disabled={sending} data-testid="scan-submit-btn"
                          className="inline-flex items-center gap-2 rounded-sm bg-neon px-7 py-4 font-bold text-obsidian shadow-neon hover:bg-neon-hover disabled:opacity-50 transition-colors">
                    {sending ? "Sending…" : "Send My Scan"} <ArrowRight size={18} />
                  </button>
                  <button onClick={() => nav("/customiser")} className="rounded-sm border border-white/20 px-7 py-4 font-bold hover:border-neon hover:text-neon transition-colors">
                    Open 3D Customiser
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="mt-6 flex items-start gap-2 font-mono text-xs text-zinc-600">
          <Upload size={14} className="mt-0.5 shrink-0" /> Your photos are sent to the Speedy team with your enquiry. Nothing is published.
        </p>
      </div>
    </div>
  );
}
